import React, { useState, useCallback, useEffect, useContext } from 'react'
import Router from 'next/router'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import moment from 'moment'

import { Row, Column } from '../Grid'
import { Button } from '../Button'
import { HoursCalendar } from './HoursCalendar'
import { DaysCalendar } from '../CalendarCartVariant'
import { Loader } from '../Loader'
import InnerNotification from '../InnerNotification'
import WarningToast from './WarningToast'
import { sendAddToCartEvent, sendSingleClickPurchase } from 'components/shared/DataLayer'

import { useScheduleCartAppointment, useSingleClickPurchase } from 'services/schedule'
import { getRefreshToken, refreshTokenPatient } from 'services/auth'
import { checkIsMobile, getDataContractId, getGAIds } from 'utils/helpers'
import TimezoneContext from 'context/timezoneContext'
import { useUser } from 'providers/UserProvider'
import { useCreditCards } from 'providers/CreditCardsProvider'

import { SCHEDULE_CART_CONSULTATION_ERRORS } from 'utils/constants'

const Calendar = ({
  schedules,
  selectedTime,
  consultationAmount,
  isLoading,
  handleDelete,
  showWarnings,
  isSearch,
  isPsychologist,
  currentSchedule,
  clinicId,
  matchCategory,
  consultationsData,
  daysQuantity,
  selectedEdit,
  mobileSelect,
  setSelectedEdit,
  selectedDate,
  setSelectedDate,
  isPostEvaluation,
  setIsDisabledLoading,
  handleRemoveConsultation,
  psychologistInfo,
  showBookmarks,
  benefitInfo,
  showNotifications,
  handleMultipleScheduling
}) => {
  const { isAllowedToPurchaseWithCreditCard } = useCreditCards()

  const [selected, setSelected] = useState([])
  const [checkoutData, setCheckoutData] = useState([])
  const schedule = schedules?.schedule
  selectedTime(selected)

  const { user } = useUser()
  const { timezone } = useContext(TimezoneContext)

  const formatSuccessData = useCallback(
    checkout => {
      const priceValue = checkoutData.priceValue || 0
      const priceCurrency = checkoutData.priceCurrency || 'BRL'

      const consultationData = {
        professional: {
          ...psychologistInfo,
          name: psychologistInfo?.name || '',
          prof_register: psychologistInfo?.crp || psychologistInfo?.prof_register || '',
          social_thumb: psychologistInfo?.avatar || psychologistInfo?.social_thumb || '',
          crp: psychologistInfo?.crp || psychologistInfo?.prof_register || ''
        },
        occurrence: {
          date: checkoutData.date,
          time: checkoutData.time,
          price: {
            value: priceValue,
            currency: priceCurrency
          },
          is_teleconf: checkoutData.is_teleconf || true
        },
        checkout: {
          consultation_value: priceValue,
          total_value: priceValue,
          balance: {
            billing_account_use: checkout?.balance?.billing_account_use || 0,
            b2b_use: checkout?.balance?.b2b_use || false,
            coupon_use: checkout?.balance?.coupon_use || false,
            total_value: {
              to_be_paid: checkout?.balance?.total_value?.to_be_paid || priceValue
            }
          },
          consultations: [
            {
              date: checkoutData.date,
              time: checkoutData.time,
              professional: {
                ...psychologistInfo,
                prof_register: psychologistInfo?.crp || psychologistInfo?.prof_register || ''
              }
            }
          ]
        }
      }
      return {
        status: 'Pagamento Autorizado',
        payment_method: checkout?.payments?.[0]?.payment_method || 'Cartão de Crédito',
        payment: {
          bank_slip: checkout?.payment?.bank_slip || null,
          pix: checkout?.payment?.pix || null,
          url: checkout?.payment?.url || null
        },
        consultation_amount: 1,
        consultations: [consultationData],
        benefit: checkout?.balance?.b2b_use || false,
        coupon_use: checkout?.balance?.coupon_use || false,
        total_amount: priceValue,
        isMonthplan: checkout?.origin === 'Plano mensal',
        balance: {
          b2b_use: checkout?.balance?.b2b_use || false,
          coupon_use: checkout?.balance?.coupon_use || false,
          total_value: {
            to_be_paid: checkout?.balance?.total_value?.to_be_paid || priceValue
          }
        }
      }
    },
    [checkoutData, psychologistInfo]
  )
  const handleOneClickSuccess = useCallback(
    response => {
      const checkout = response?.data?.content?.checkout || response?.data?.checkout
      const formattedData = formatSuccessData(checkout)

      if (isNaN(formattedData.total_amount)) {
        formattedData.total_amount = checkoutData.priceValue || 0
      }
      if (formattedData.balance && isNaN(formattedData.balance.total_value.to_be_paid)) {
        formattedData.balance.total_value.to_be_paid = checkoutData.priceValue || 0
      }

      sendSingleClickPurchase({
        value: formattedData.total_amount,
        items: [
          {
            item_id: formattedData.consultations[0]?.professional?.id,
            item_name: formattedData.consultations[0]?.professional?.name,
            price: formattedData.total_amount
          }
        ]
      })
      sessionStorage.setItem('checkout_payment', JSON.stringify(formattedData))
      Router.push('/agendamento-realizado')
    },
    [formatSuccessData, checkoutData]
  )

  const schedulingOptions = {
    onSuccess: ({ checkout }) => {
      Router.push({
        pathname: '/agendar-consultas/finalizar-pagamento',
        query: {
          checkoutId: checkout?.id,
          timezone: checkout?.consultations_success?.[0]?.ocurrence?.timezone || timezone,
          newCheckout: 'generateNewCheckout'
        }
      })
    },
    onError: error => {
      const errorMessage = handleSchedulingError(error)

      if (error?.response?.data?.log_content?.traceback?.includes('NotAuthenticated')) {
        return handleAuthenticationError(false)
      }

      toast.error(errorMessage, { autoClose: false })
    }
  }

  const {
    isLoading: isSingleClickPurchasing,
    mutateAsync: singleClickPurchaseAsync
  } = useSingleClickPurchase(user?.id, {
    onSuccess: handleOneClickSuccess,
    onError: error => {
      const errorCode = error?.response?.data?.log_content?.error_code

      toast.error(
        SCHEDULE_CART_CONSULTATION_ERRORS?.[errorCode] || SCHEDULE_CART_CONSULTATION_ERRORS.default,
        {
          autoClose: 5000
        }
      )
    }
  })

  const {
    isLoading: isSchedulingAppointment,
    mutateAsync: scheduleAsync
  } = useScheduleCartAppointment(user?.id, schedulingOptions)

  const isMonthDifferent = (date1, date2) => {
    return moment(date1).month() !== moment(date2).month()
  }

  const sendDatalayer = () => {
    const professionalDataForDataLayer = [
      {
        item_name: checkoutData?.psico_name,
        item_id: checkoutData?.psico_id,
        price: checkoutData?.priceValue,
        quantity: 1
      }
    ]
    const value = checkoutData?.priceValue

    sendAddToCartEvent(professionalDataForDataLayer, value)
  }

  const handleScheduleSelection = useCallback(
    (hour, checkoutData = false) => {
      if (hour.status !== 'available') {
        return
      }

      const formattedDateTime = moment(hour.date + hour.time, 'YYYY-MM-DD HH:mm').format(
        'YYYY-MM-DD HH:mm'
      )

      const isCorporateConsultation = isMonthDifferent(hour.date, moment().format('YYYYMMD'))
        ? hour?.contract_price?.value === '0.00' || !showBookmarks
        : (selected.length < benefitInfo?.total - benefitInfo?.used &&
            (hour?.contract_price?.value === '0.00' || !showBookmarks)) ||
          benefitInfo?.frequency === ''

      const newSelected = {
        id: hour.id,
        start: formattedDateTime,
        date: hour.date,
        time: hour.time,
        is_corporate_consultation: isCorporateConsultation,
        timezone: timezone,
        schedule_id: hour.schedule_id,
        isoWeek: moment(hour.date)
          .clone()
          .isoWeek()
      }

      setSelected(isSearch ? [newSelected] : [...selected, newSelected])

      if (checkoutData) {
        setCheckoutData({
          ...currentSchedule,
          priceCurrency: hour.price.currency,
          priceValue: hour.price.value,
          id: hour.id,
          date: hour.date,
          time: hour.time,
          schedule_id: hour.schedule_id,
          clinicId: clinicId,
          matchCategory: matchCategory
        })
      }
    },
    [selected, selectedTime, currentSchedule]
  )

  const handleSchedulingError = error => {
    if (error.response?.data?.log_content?.error_code === 'professional_not_in_partnership') {
      return 'Infelizmente, esse psicólogo não atende o seu benefício corporativo'
    }
    if (
      error.response?.data?.log_content?.error_code === 'schedule_conflicts' ||
      error.response?.data?.log_content?.error_code === 'unavailable_time'
    ) {
      return 'O horário selecionado não está mais disponível. Por favor, tente outro horário.'
    }
    if (error.response?.data?.log_content?.error_code === 'contract_use_exceeded') {
      return 'Você atingiu o número máximo de consultas do seu benefício. Para mais informações sobre limitações de consultas, entre em contato com a sua empresa.'
    }
    return 'Ocorreu um problema, tente novamente mais tarde'
  }

  const handleAuthenticationError = async (isSingleClickPurchase = false) => {
    const refreshToken = getRefreshToken()
    if (refreshToken) {
      const {
        data: {
          content: { access_token }
        }
      } = await refreshTokenPatient(refreshToken)

      if (access_token) {
        return makeSchedule(isSingleClickPurchase)
      }
    } else {
      Router.push({
        pathname: '/sou-paciente-carrinho',
        query: checkoutData
      })
    }
  }

  const makeSchedule = async (isSingleClickPurchase = false) => {
    try {
      sendDatalayer()

      if (!user?.id) {
        const dataContractId = getDataContractId()
        const contractId = dataContractId?.integration?.contract?.id
        const presignupToken = dataContractId?.token

        Router.push({
          pathname: '/sou-paciente-carrinho',
          query: {
            ...checkoutData,
            ...(presignupToken && { presignup_token: presignupToken }),
            ...(contractId && { contract_id: contractId })
          }
        })
        return
      }

      const payload = {
        amount: 1,
        schedule_id: clinicId,
        consultations: [
          {
            professional_id: checkoutData.psico_id,
            start: selected[0].start,
            date: checkoutData.date,
            time: checkoutData.time,
            timezone: checkoutData.timezone === '' ? timezone : checkoutData.timezone,
            schedule_id: checkoutData.schedule_id,
            available_teleconf: checkoutData.is_teleconf || true
          }
        ],
        ...getGAIds()
      }

      if (isSingleClickPurchase) {
        await singleClickPurchaseAsync(payload)
      } else {
        await scheduleAsync(payload)
      }
    } catch (error) {
      console.error('Erro no agendamento:', error)
    }
  }

  useEffect(() => {
    if (showWarnings && selected.length >= 2) {
      const lastWeek = selected[selected.length - 1].isoWeek
      const secondLastWeek = selected[selected.length - 2].isoWeek

      if (lastWeek === secondLastWeek) {
        toast.info(WarningToast, {
          position: 'bottom-left',
          closeOnClick: true,
          autoClose: 3000
        })
      }
    }
  }, [selected, showWarnings])

  return (
    <Column width='fit-content' height='100%' justifyContent='center' alignItems='center'>
      <Row>
        {!schedules?.appointments?.length || isLoading || !schedules ? (
          <Row width='100%' height='316px' justifyContent='center' alignItems='center'>
            <Image src='/static/images/match-maker/loader.svg' alt='loading' />
          </Row>
        ) : (
          <Column width='100%' flexWrap='wrap'>
            <Column width='100%' height={isSearch ? '250px' : 'auto'}>
              <DaysCalendar
                isSearch={isPostEvaluation || isSearch}
                isPsychologist={isPsychologist}
                appointments={
                  isSearch
                    ? schedules?.appointments.slice(0, 5)
                    : schedules?.appointments.slice(0, daysQuantity)
                }
                schedule={schedule}
              />
              <HoursCalendar
                user={user}
                daysQuantity={daysQuantity}
                consultationsData={consultationsData}
                isSearch={isPostEvaluation || isSearch}
                showWarnings={showWarnings}
                consultationAmount={consultationAmount}
                schedule={schedule}
                selectSchedule={(...params) => handleScheduleSelection(...params, true)}
                selected={selected}
                handleDelete={isPostEvaluation || isSearch ? () => setSelected([]) : handleDelete}
                selectedEdit={selectedEdit}
                setSelectedEdit={setSelectedEdit}
                mobileSelect={mobileSelect}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                handleRemoveConsultation={handleRemoveConsultation}
                setIsDisabledLoading={setIsDisabledLoading}
                psychologistInfo={psychologistInfo}
                showBookmarks={showBookmarks}
                benefitInfo={benefitInfo}
              />
            </Column>
          </Column>
        )}
      </Row>

      {isSearch && (
        <Column width='100%' mt='24px' alignItems='flex-start'>
          {!!user && (
            <Button
              height='48px'
              variant='text'
              width='100%'
              mb='8px'
              onClick={() => handleMultipleScheduling(psychologistInfo)}
            >
              Agendar múltiplas consultas
            </Button>
          )}

          <Row width='100%'>
            {isAllowedToPurchaseWithCreditCard && (
              <Button
                disabled={selected.length === 0 || isSingleClickPurchasing}
                variant='primary'
                width='100%'
                height='48px'
                fontSize='12px'
                mr='16px'
                onClick={() => makeSchedule(true)}
              >
                {isSingleClickPurchasing ? <Loader size={24} /> : 'Agendar com 1-clique'}
              </Button>
            )}

            <Button
              disabled={selected.length === 0 || isSchedulingAppointment}
              variant={isAllowedToPurchaseWithCreditCard ? 'primary-outlined' : 'primary'}
              width='100%'
              height='48px'
              onClick={() => makeSchedule()}
            >
              {isSchedulingAppointment ? <Loader size={24} /> : 'Agendar'}
            </Button>
          </Row>
        </Column>
      )}

      {(showNotifications || isPsychologist || checkIsMobile(769)) && (
        <InnerNotification
          dataType={'billet-notice'}
          selectedDate={selectedDate}
          setSelectedDate={value => this.setState({ selectedDate: value })}
          variant={isPostEvaluation ? 'postEvaluation' : 'inCalendar'}
        />
      )}
    </Column>
  )
}

Calendar.defaultProps = {
  selectedTime: () => {},
  handleDelete: () => {}
}

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

const Image = styled.img`
  width: 60px;
  animation: ${rotate} 2s linear infinite;
`

export { Calendar }
